import type { DirectoryElementsWithPermissions, DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { ZustandHookSelectors } from 'auto-zustand-selectors-hook';

import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { FilesStoreAction, FilesStoreState, FSEntityType } from '.';

const useFilesStorageBase = create<FilesStoreAction & FilesStoreState>()(
  persist(
    (set, get) => ({
      model: 'MY_DRIVE',
      selected: { files: [], directories: [] },
      permissions: { read: false, write: false, edit: false },
      openDelete: false,
      fileToOpen: undefined,
      openViewer: false,
      openEditName: false,
      openShare: false,
      openMove: false,
      openDownload: false,
      openMoreInfo: false,
      openCreateFile: false,
      openCreateFolder: false,
      openCreateDir: false,

      // Focus and keyboard navigation state
      focusedIndex: null,
      anchorIndex: null,
      allItems: [],

      setModel: (payload) => set({ model: payload }),
      setFileToOpen: (payload) => set({ fileToOpen: payload }),
      setSelected: (payload) => set({ selected: payload }),
      resetSelected: () =>
        set({
          selected: { files: [], directories: [] },
          focusedIndex: null,
          anchorIndex: null,
        }),
      isSelected: (id, variant) => {
        const { selected } = get();
        switch (variant) {
          case 'file':
            return selected.files.some((element) => element.id === id);
          case 'directory':
            return selected.directories.some((element) => element.id === id);
          default:
            return selected.files.some((element) => element.id === id) || selected.directories.some((element) => element.id === id);
        }
      },
      selectedSize: (variant) => {
        const { selected } = get();
        switch (variant) {
          case 'file':
            return selected.files.length || 0;
          case 'directory':
            return selected.directories.length || 0;
          default:
            return selected.files.length + selected.directories.length || 0;
        }
      },
      setPermissions: (payload) => set({ permissions: payload }),
      setOpenViewer: (open: boolean) => set({ openViewer: open }),
      setOpenEditName: (open: boolean) => set({ openEditName: open }),
      setOpenShare: (open: boolean) => set({ openShare: open }),
      setOpenMove: (open: boolean) => set({ openMove: open }),
      setOpenDownload: (open: boolean) => set({ openDownload: open }),
      setOpenMoreInfo: (open: boolean) => set({ openMoreInfo: open }),
      setOpenDelete: (open: boolean) => set({ openDelete: open }),
      setOpenCreateFile: (open: boolean) => set({ openCreateFile: open }),
      setOpenCreateFolder: (open: boolean) => set({ openCreateFolder: open }),
      setOpenCreateDir: (open: boolean) => set({ openCreateDir: open }),

      // Focus and keyboard navigation actions
      setFocusedIndex: (index) => set({ focusedIndex: index }),
      setAnchorIndex: (index) => set({ anchorIndex: index }),
      setAllItems: (items) => set({ allItems: items }),

      onFSEntityClick: ({ file, directory, event, mode = 'replace', itemIndex }: FSEntityType) => {
        const { selected, isSelected, setSelected, allItems, anchorIndex } = get();

        const element = file ?? directory;

        if (!element) return;

        const entityCollectionKey = file ? 'files' : 'directories';
        const selectionVariant = file ? 'file' : 'directory';
        const elementIsSelected = isSelected(element.id, selectionVariant);

        // Find item index if not provided
        const actualItemIndex = itemIndex ?? allItems.findIndex((item) => item.id === element.id);

        const cloneSelection = (): DirectoryElementsWithPermissions => ({
          files: [...selected.files],
          directories: [...selected.directories],
        });

        const addElement = (source: DirectoryElementsWithPermissions): DirectoryElementsWithPermissions => {
          if (entityCollectionKey === 'files') {
            const alreadyPresent = source.files.some((el) => el.id === element.id);
            if (!alreadyPresent) {
              source.files.push(element as FilePublicWithPermissions);
            }
          } else {
            const alreadyPresent = source.directories.some((el) => el.id === element.id);
            if (!alreadyPresent) {
              source.directories.push(element as DirectoryPublic);
            }
          }

          return source;
        };

        const removeElement = (source: DirectoryElementsWithPermissions): DirectoryElementsWithPermissions => {
          if (entityCollectionKey === 'files') {
            source.files = source.files.filter((el) => el.id !== element.id);
          } else {
            source.directories = source.directories.filter((el) => el.id !== element.id);
          }

          return source;
        };

        const toggleSelection = () => {
          const base = cloneSelection();
          return elementIsSelected ? removeElement(base) : addElement(base);
        };

        const ensureSelected = () => {
          if (elementIsSelected) {
            return null;
          }

          const base = cloneSelection();
          return addElement(base);
        };

        const replaceSelection = (): DirectoryElementsWithPermissions => ({
          files: entityCollectionKey === 'files' ? [element as FilePublicWithPermissions] : [],
          directories: entityCollectionKey === 'directories' ? [element as DirectoryPublic] : [],
        });

        const replaceOrClearSelection = () => {
          const isSingleSelectionForElement = elementIsSelected
            ? entityCollectionKey === 'files'
              ? selected.files.length === 1 && selected.files[0]?.id === element.id
              : selected.directories.length === 1 && selected.directories[0]?.id === element.id
            : false;

          if (isSingleSelectionForElement) {
            return { files: [], directories: [] };
          }

          return replaceSelection();
        };

        // Ctrl/Cmd + Click: Toggle selection, update anchor
        if (event?.ctrlKey || event?.metaKey) {
          setSelected(toggleSelection());
          set({
            focusedIndex: actualItemIndex >= 0 ? actualItemIndex : null,
            anchorIndex: actualItemIndex >= 0 ? actualItemIndex : null,
          });
          return;
        }

        // Shift + Click: Range selection from anchor to clicked item
        if (event?.shiftKey && actualItemIndex >= 0) {
          // Use anchorIndex if set, otherwise use focusedIndex, otherwise use 0
          const startIndex = anchorIndex ?? 0;
          const start = Math.min(startIndex, actualItemIndex);
          const end = Math.max(startIndex, actualItemIndex);

          const files: FilePublicWithPermissions[] = [];
          const directories: DirectoryPublic[] = [];

          for (let i = start; i <= end; i++) {
            const item = allItems[i];
            if (!item) continue;

            if (getIsFSEntityFile(item)) {
              files.push(item as FilePublicWithPermissions);
            } else {
              directories.push(item as DirectoryPublic);
            }
          }

          setSelected({ files, directories });
          // Update focusedIndex but NOT anchorIndex (anchor stays fixed for shift selections)
          set({ focusedIndex: actualItemIndex });
          return;
        }

        // Regular click (no modifiers)
        switch (mode) {
          case 'toggle':
            setSelected(toggleSelection());
            break;
          case 'ensure-selected': {
            const updated = ensureSelected();
            if (updated) {
              setSelected(updated);
            }
            break;
          }
          case 'replace-or-clear':
            setSelected(replaceOrClearSelection());
            break;
          default:
            setSelected(replaceSelection());
            break;
        }

        // Update both focusedIndex and anchorIndex for non-shift clicks
        if (actualItemIndex >= 0) {
          set({
            focusedIndex: actualItemIndex,
            anchorIndex: actualItemIndex,
          });
        }
      },
    }),
    {
      name: 'files-storage',
      partialize: (state) => ({
        model: state.model,
      }),
    }
  )
);

export const useFilesStorage = createSelectorHooks(useFilesStorageBase) as typeof useFilesStorageBase & ZustandHookSelectors<FilesStoreAction & FilesStoreState>;
